/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { PageBreadcrumbs } from '@/components/page-breadcrumbs';
import type { ShopperProducts } from '@/scapi';
import { routes, routeHref } from '@/route-paths';

type PathRecord = Required<ShopperProducts.schemas['Category']>['parentCategoryTree'][0];

export default function CategoryBreadcrumbs({
    category,
}: {
    category: ShopperProducts.schemas['Category'];
}): ReactElement {
    const { t } = useTranslation('category');
    const items: PathRecord[] = category.parentCategoryTree ?? [{ id: category.id, name: category.name }];
    // Last category is the current page (bold, not a link), like every other breadcrumb on the site
    return (
        <PageBreadcrumbs
            ariaLabel={t('breadcrumbs.label')}
            items={[
                { label: t('breadcrumbs.home'), to: routes.home },
                ...items.map((item, index) => ({
                    label: item.name ?? '',
                    to:
                        index === items.length - 1
                            ? undefined
                            : routeHref(routes.category, { categoryId: item.id ?? '' }),
                })),
            ]}
        />
    );
}
